import {
  Dropdown,
  Icon,
  InputMasked,
  InputText,
  RadioButton,
} from "@uigovpe/components";
import { Controller, useFormContext } from "react-hook-form";
import type { ProfileFormData } from "../../../types";
import { onlyDigits } from "@/utils/formats";
import LockedField from "../locked-field";

const disabilityOptions = [
  "Física",
  "Auditiva",
  "Visual",
  "Intelectual",
  "Múltipla",
].map((value) => ({ label: value, value }));

export default function ProfileStepOne() {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ProfileFormData>();
  const isDisabledPerson = watch("isDisabledPerson");
  const complementaryInfoGridClass = isDisabledPerson
    ? "mt-2! grid! grid-cols-1! gap-6! lg:grid-cols-[minmax(130px,180px)_minmax(260px,1fr)_minmax(150px,190px)]! lg:items-center!"
    : "mt-2! grid! grid-cols-1! gap-6! sm:grid-cols-[minmax(130px,180px)_minmax(150px,190px)]! sm:items-center! sm:gap-12!";

  const updatePcd = (value: boolean) => {
    setValue("isDisabledPerson", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (!value) setValue("disabilityType", "", { shouldDirty: true });
  };

  return (
    <div className="flex! w-full! flex-col! gap-8!">
      <section className="flex! flex-col! gap-5!">
        <div className="flex! flex-col! gap-2!">
          <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
            <Icon icon="person" className="text-primary!" />
            <h3 className="m-0! text-base! font-bold!">Identificação</h3>
          </div>
          <p className="m-0! text-sm! text-zinc-700!">
            Informe seus dados principais para identificarmos seu cadastro com
            segurança.
          </p>
        </div>

        <div className="grid! grid-cols-1! gap-x-6! gap-y-4! md:grid-cols-2!">
          <LockedField id="profile-full-name-locked" showLockIcon tooltip="Este dado é fornecido pela autenticação e não pode ser alterado.">
            <InputText
              label="Nome completo"
              disabled
              supportText="Como você gostaria de ser identificada na plataforma."
              className="w-full!"
              {...register("fullName")}
            />
          </LockedField>
          <LockedField id="profile-email-locked" showLockIcon tooltip="Este dado é fornecido pela autenticação e não pode ser alterado.">
            <InputText
              label="E-mail"
              type="email"
              disabled
              supportText="E-mail vinculado à sua autenticação na plataforma."
              className="w-full!"
              {...register("email")}
            />
          </LockedField>
          <LockedField id="profile-cpf-locked" showLockIcon tooltip="Este dado é fornecido pela autenticação e não pode ser alterado.">
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <InputMasked
                  label="CPF"
                  mask="999.999.999-99"
                  value={field.value}
                  disabled
                  supportText="Usado apenas para identificação. Não será público."
                  className="w-full!"
                  onChange={() => undefined}
                />
              )}
            />
          </LockedField>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: "Informe seu celular",
              validate: (value) =>
                onlyDigits(value).length === 11 ||
                "Informe um celular completo",
            }}
            render={({ field, fieldState }) => (
              <InputMasked
                label="Celular"
                mask="(99) 9 9999-9999"
                placeholder="(81) 9 0000-0000"
                value={field.value}
                supportText={
                  fieldState.error?.message ??
                  "Informe um número ativo para contato."
                }
                invalid={Boolean(fieldState.error)}
                className="w-full!"
                onBlur={field.onBlur}
                onChange={(event) =>
                  field.onChange(onlyDigits(event.target.value))
                }
              />
            )}
          />
        </div>
      </section>

      <section className="flex! flex-col! gap-5!">
        <div className="flex! flex-col! gap-2!">
          <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
            <Icon icon="volunteer_activism" outline className="text-primary!" />
            <h3 className="m-0! text-base! font-bold!">
              Informações complementares
            </h3>
          </div>
          <p className="m-0! text-sm! text-zinc-700!">
            Esses dados ajudam a Secretaria a conhecer melhor as empreendedoras
            cadastradas.
          </p>
        </div>
        <div className={complementaryInfoGridClass}>
          <fieldset className="flex! min-w-0! flex-col!">
            <legend className="mb-2! text-sm! font-medium!">É PCD?</legend>
            <div className="flex! gap-5!">
              <RadioButton
                label="Sim"
                name="pcd"
                value="yes"
                checked={isDisabledPerson}
                onChange={() => updatePcd(true)}
              />
              <RadioButton
                label="Não"
                name="pcd"
                value="no"
                checked={!isDisabledPerson}
                onChange={() => updatePcd(false)}
              />
            </div>
          </fieldset>
          {isDisabledPerson && (
            <Controller
              name="disabilityType"
              control={control}
              rules={{ required: "Selecione o tipo de deficiência" }}
              render={({ field, fieldState }) => (
                <Dropdown
                  label="Qual o tipo de deficiência?"
                  value={field.value}
                  options={disabilityOptions}
                  placeholder="Selecione o tipo"
                  supportText={fieldState.error?.message}
                  invalid={Boolean(fieldState.error)}
                  className="w-full!"
                  onChange={(event) => field.onChange(event.value)}
                />
              )}
            />
          )}
          <fieldset
            className={`flex! min-w-0! flex-col! ${
              isDisabledPerson ? "lg:justify-self-end!" : ""
            }`}
          >
            <legend className="mb-2! text-sm! font-medium!">
              É mãe atípica?
            </legend>
            <div className="flex! gap-5!">
              <RadioButton
                label="Sim"
                name="atypical"
                value="yes"
                checked={watch("isAtypicalMother")}
                onChange={() =>
                  setValue("isAtypicalMother", true, { shouldDirty: true })
                }
              />
              <RadioButton
                label="Não"
                name="atypical"
                value="no"
                checked={!watch("isAtypicalMother")}
                onChange={() =>
                  setValue("isAtypicalMother", false, { shouldDirty: true })
                }
              />
            </div>
          </fieldset>
        </div>
      </section>
      {errors.root && (
        <p className="m-0! text-sm! text-red-700!">{errors.root.message}</p>
      )}
    </div>
  );
}
